import { DocumentNode, useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { Data } from 'components/Charts/types'
import { typeOfStoriesPage } from 'hooks/usePurchasedBrands'
import { CROSS_SINGLE_QUERY, SOLITARY_MULTI_QUERY, SOLITARY_SINGLE_QUERY } from 'query/query'
import { useAppliedFiltersAndContext } from 'hooks/useAppliedFiltersAndContext'
import { PageNameEnums } from 'hooks/useKBQDropdownOptions'
import { Cell } from 'reducers/subscription/user'
import { InsightData } from 'containers/charts'
import { Filter } from 'typings/filters'

type Options = {
  answerCodes?: number[]
  limitDataPoints?: number
  query?: DocumentNode
  kbqSelect?: boolean
  context?: Cell[] | null
}

export enum EligibleVariable {
  BRANDS = 'purchasedBrand',
  RETAILERS = 'purchaseRetailer',
  INCOME = 'income',
  HOUSEHOLD_COMPOSITION = 'householdComposition',
  AD_TRIGGERS = 'advertTriggers',
  RETAILER_DRIVER = 'retailerDriver',
  CONSIDERED = 'consideredBrands',
  BRAND_LOST_SHOPPER = 'brandLostShopper',
  OnlineVisits = 'totalOnlineVisits',
  InStoreVisits = 'totalInStoreVisits',
  TotalVisits = 'totalVisited',
  previousBrand = 'previousBrand',
}
export const getKBQSelectedQuery = () => {
  const pathName = window.location.pathname.split('/').pop()
  if (typeOfStoriesPage?.includes(pathName as string)) {
    return SOLITARY_SINGLE_QUERY
  }
  if (PageNameEnums.RetailerLostShoppers === pathName) {
    return SOLITARY_MULTI_QUERY
  }
}
export const OTHER_UNCLASSIFIED_ANSWER_CODE = 98 // For both Brands and Retailers

const getEffectiveValues = (
  insightData: { insightData: InsightData } | undefined,
  context: Cell[],
  appliedFilters: Filter[]
) => {
  const effectiveContext = insightData?.insightData?.graphqlVars.context ?? context
  const effectiveFilters = insightData?.insightData?.graphqlVars?.filters ?? appliedFilters
  return { effectiveContext, effectiveFilters }
}

export const getKBQVariablesAndQuery = (
  kbqSelect?: boolean,
  KBQSelectedQuery?: any,
  kbqVariables?: any,
  variables?: any
) => {
  const vars = kbqSelect ? kbqVariables : variables
  const selectQuery = kbqSelect ? KBQSelectedQuery : CROSS_SINGLE_QUERY
  return { vars, selectQuery }
}
export const useSegmentsBy = (
  variable: EligibleVariable,
  { answerCodes, limitDataPoints, query, kbqSelect }: Options = {},
  secondaryPrimaryVar?: EligibleVariable,
  inView?: boolean,
  insightData?: { insightData: InsightData }
) => {
  const { appliedFilters: filters, context } = useAppliedFiltersAndContext()
  const { effectiveContext, effectiveFilters } = getEffectiveValues(
    insightData,
    context as Cell[],
    filters as Filter[]
  )
  const supportingVariables = useMemo(
    () => ({
      ...(limitDataPoints !== undefined
        ? {
            limitDataPoints,
            removeDataPoints: !(answerCodes ?? []).length ? [OTHER_UNCLASSIFIED_ANSWER_CODE] : [],
          }
        : {}),
      ...(answerCodes
        ? {
            showDataPoints: answerCodes,
            limitDataPoints: limitDataPoints ?? answerCodes.length,
          }
        : {}),

      filters: effectiveFilters,
      context: effectiveContext,
    }),
    [answerCodes, effectiveContext, effectiveFilters, limitDataPoints]
  )
  const variables = useMemo(
    () => ({
      primaryVar: 'shopperClusterSegmentation',
      secondaryVar: variable,
      secondPrimaryVar: secondaryPrimaryVar,
      ...supportingVariables,
    }),
    [secondaryPrimaryVar, variable, supportingVariables]
  )
  const kbqVariables = useMemo(
    () => ({
      primaryVar: variable,
      ...supportingVariables,
    }),
    [supportingVariables, variable]
  )
  const KBQSelectedQuery = getKBQSelectedQuery()
  const { vars, selectQuery } = getKBQVariablesAndQuery(
    kbqSelect,
    KBQSelectedQuery,
    kbqVariables,
    variables
  )
  const { loading, error, data } = useQuery<Data>(query ?? selectQuery, {
    variables: vars,
    skip: inView !== undefined && !inView,
  })
  return { data, error: !data ? error : undefined, loading, vars }
}
